import { isObject, isString, isEmpty } from "lodash";
import { ls } from "./localStorage";

class Authorization {
  constructor() {
    this.authUser = null;
    this.authUserId = null;
    this.authRole = null;
    this.sessionTimer = null;
    this.sessionExpireTime = 1000 * 60 * 10; // 10 minutes idle timeout
  }

  /**
   * Set auth user details into class property
   */
  setAuthUser() {
    const user = ls.getItem("authorizedUserDetails");
    try {
      this.authUser = user ? JSON.parse(user) : null;
    } catch (e) {
      this.authUser = null;
    }
  }

  /**
   * Check if user is logged in (valid token exists)
   */
  isLoggedIn() {
    try {
      const user = ls.getItem("authorizedUserDetails");
      if (!user) return false;

      const parsed = JSON.parse(user);
      return parsed && isString(parsed.token) && !isEmpty(parsed.token);
    } catch (e) {
      return false;
    }
  }

  /**
   * Get logged-in user details
   */
  getAuthUser() {
    if (this.isLoggedIn()) {
      this.setAuthUser();
    }
    return this.authUser;
  }

  /**
   * Get auth user identifier
   */
  getAuthUserId() {
    const user = this.getAuthUser();
    return isObject(user) && user.id ? user.id : 0;
  }

  getAuthTmcId() {
    const user = this.getAuthUser();
    return isObject(user) && user.tmcId ? user.tmcId : 0;
  }

  /**
   * Get auth user role Id
   */
  getAuthRoleId() {
    const user = this.getAuthUser();
    return isObject(user) && user.roleId ? user.roleId : 0;
  }

  /**
   * Get auth user approval status
   */
  getUserApproval() {
    const user = this.getAuthUser();
    return isObject(user) && user.bookingApproval
      ? user.bookingApproval
      : false;
  }

  /**
   * Get auth user matrix
   */
  getUserMatrix() {
    const user = this.getAuthUser();
    return isObject(user) && user.accessMatrix ? user.accessMatrix : false;
  }

  /**
   * Get authentication access token
   */
  getAccessToken() {
    const authUser = this.getAuthUser();
    return authUser && isString(authUser.token) ? authUser.token : null;
  }

  /**
   * Login the user by setting it in local storage
   */
  login(userDetails) {
    if (typeof Storage !== "undefined") {
      ls.removeItem("authorizedUserDetails");
      ls.setItem("authorizedUserDetails", JSON.stringify(userDetails));
    } else {
      console.error("local storage is not supported");
    }
  }

  /**
   * Logout the user and clear storage
   */
  logout() {
    if (typeof Storage !== "undefined") {
      ls.removeItem("authorizedUserDetails");
      ls.removeItem("userLandingPath");
      ls.removeItem("branchAccess");
      ls.removeItem("branchAccessSelected");
      window.location.reload();
    } else {
      console.error("local storage is not supported");
    }
  }

  /**
   * Set and get branch access
   */
  setBranchAccess(branchAccess) {
    ls.removeItem("branchAccess");
    ls.setItem("branchAccess", JSON.stringify(branchAccess));
  }

  getBranchAccess() {
    const branch = ls.getItem("branchAccess");
    return branch ? JSON.parse(branch) : null;
  }

  setBranchAccessSelected(branchAccess) {
    ls.removeItem("branchAccessSelected");
    ls.setItem("branchAccessSelected", JSON.stringify(branchAccess));
  }

  getBranchAccessSelected() {
    const branch = ls.getItem("branchAccessSelected");
    return branch ? JSON.parse(branch) : null;
  }

  /**
   * Once user is logged in, redirect to given path
   */
  redirectAfterLogin(redirectPath = "/trips") {
    ls.setItem("userLandingPath", redirectPath);
    document.location = redirectPath;
  }

  /**
   * Return the landing page path of user
   */
  getUserLandingPath() {
    return !isEmpty(ls.getItem("userLandingPath"))
      ? ls.getItem("userLandingPath")
      : "";
  }

  /**
   * Reset the idle time based on listed actions
   */
  idleTimer() {
    let self = this;

    const sessionExpire = function () {
      self.logout();
      window.location.reload(); // reload the page on session expire
    };

    const resetTimer = function () {
      if (self.isLoggedIn()) {
        clearTimeout(self.sessionTimer);
        self.sessionTimer = setTimeout(sessionExpire, self.sessionExpireTime);
      }
    };

    // Listeners for user activity
    window.onmousemove = resetTimer;
    window.onmousedown = resetTimer;
    window.onclick = resetTimer;
    window.onscroll = resetTimer;
    window.onkeypress = resetTimer;
  }
}

export default new Authorization();
